import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { ArrowRight } from "lucide-react";
import { Botao, CampoDeTexto, cn } from "@base/ui";
import { useAuth } from "./auth.contexto";

const VIDEO_WALLPAPER = "/login-wallpaper.mp4";
const POSTER_WALLPAPER = "/login-creators-cover.png";
const EMAIL_SUPORTE_SENHA = "tecnologia@renkestudio.com.br";

/**
 * Login Creative OS.
 *
 * Soft UI / Connect: coluna muted unificada, form sem card branco isolado.
 * Video em loop nativo (seek reverso em H.264 trava; sem arquivo invertido).
 * Wordmark Creative OS (public/CreativeOS.png). Copy com acentuacao.
 */

export function PaginaDeLogin() {
  const { entrar } = useAuth();
  const idManter = useId();
  const [email, definirEmail] = useState("");
  const [senha, definirSenha] = useState("");
  const [manterConectado, definirManterConectado] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const [avisoSenha, definirAvisoSenha] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    definirErro(null);
    definirAvisoSenha(null);
    definirEnviando(true);
    try {
      await entrar(email, senha, { manterConectado });
    } catch (causa) {
      definirErro(
        causa instanceof Error
          ? causa.message
          : "Não foi possível entrar. Confira e-mail e senha e tente de novo.",
      );
    } finally {
      definirEnviando(false);
    }
  }

  function aoEsquecerSenha() {
    definirAvisoSenha(
      `Não há reset automático ainda. Peça a redefinição para ${EMAIL_SUPORTE_SENHA}.`,
    );
  }

  const podeEnviar =
    email.trim().includes("@") && senha.trim().length >= 8 && !enviando;

  return (
    <div className="grid min-h-dvh bg-background lg:grid-cols-2">
      <section
        aria-label="Entrar"
        className="relative isolate flex flex-col"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-80 [background:var(--primary-glow)]"
        />
        <div className="relative mx-auto mt-6 h-32 w-[min(100%-3rem,26rem)] overflow-hidden rounded-2xl border border-border-subtle lg:hidden">
          <VideoWallpaper className="absolute inset-0 h-full w-full object-cover object-[center_35%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        </div>

        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 md:py-12">
          <div className="mx-auto w-full max-w-[26.25rem] animate-aparecer">
            <header className="mb-8 flex flex-col items-start text-left">
              <img
                src="/CreativeOS.png"
                alt="Creative OS"
                className="marca-escura mb-10 h-6 w-auto max-w-[min(100%,16rem)] object-contain object-left"
                decoding="async"
              />
              <h1 className="text-[32px] font-bold leading-10 tracking-[-0.02em] text-foreground">
                Bem-vindo de volta
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Entre com sua conta Renke para continuar.
              </p>
            </header>

            <form
              onSubmit={(evento) => void aoEnviar(evento)}
              className="space-y-4"
              noValidate
            >
              <CampoDeTexto
                rotulo="E-mail"
                type="email"
                autoComplete="username"
                placeholder="você@renkestudio.com.br"
                value={email}
                onChange={(evento) => definirEmail(evento.target.value)}
                required
                {...(erro ? { erros: [erro] } : {})}
              />

              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold leading-none text-foreground">
                    Senha
                  </span>
                  <button
                    type="button"
                    onClick={aoEsquecerSenha}
                    className="cursor-pointer text-[13px] font-medium text-muted-foreground transition-colors duration-200 hover:text-foreground"
                  >
                    Esqueceu a senha?
                  </button>
                </div>
                <CampoDeTexto
                  rotulo="Senha"
                  type="password"
                  autoComplete="current-password"
                  value={senha}
                  onChange={(evento) => definirSenha(evento.target.value)}
                  required
                  className="[&>label]:sr-only"
                />
              </div>

              {avisoSenha ? (
                <p role="status" className="text-[13px] leading-relaxed text-muted-foreground">
                  {avisoSenha}{" "}
                  <a
                    href={`mailto:${EMAIL_SUPORTE_SENHA}?subject=${encodeURIComponent("Redefinição de senha - Creative OS")}`}
                    className="font-medium text-foreground underline-offset-2 hover:underline"
                  >
                    Abrir e-mail
                  </a>
                </p>
              ) : null}

              <label
                htmlFor={idManter}
                className="flex cursor-pointer items-center gap-2.5 pt-1 text-[14px] text-foreground"
              >
                <input
                  id={idManter}
                  type="checkbox"
                  checked={manterConectado}
                  onChange={(evento) =>
                    definirManterConectado(evento.target.checked)
                  }
                  className="size-[18px] cursor-pointer rounded-xs accent-primary"
                />
                Manter conectado
              </label>

              <div className="pt-2">
                <Botao
                  type="submit"
                  variante="primario"
                  tamanho="grande"
                  carregando={enviando}
                  disabled={!podeEnviar}
                  className={cn(
                    "h-11 min-h-11 w-full cursor-pointer text-[15px]",
                    "disabled:cursor-not-allowed disabled:opacity-45",
                  )}
                >
                  Entrar
                  <ArrowRight aria-hidden="true" className="size-4" />
                </Botao>
              </div>
            </form>
          </div>
        </div>

        <footer className="flex justify-center px-6 pb-8">
          <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
            <span>Termos de serviço</span>
            <span aria-hidden="true">·</span>
            <span>Privacidade</span>
            <span aria-hidden="true">·</span>
            <span>© 2026 Grupo RNK</span>
          </p>
        </footer>
      </section>

      <PainelVisualStudio />
    </div>
  );
}

function PainelVisualStudio() {
  return (
    <aside
      className={cn(
        "relative hidden min-h-dvh p-3 lg:block",
        "animate-aparecer",
      )}
      aria-label="Ambiente de criação"
    >
      <div className="relative h-full overflow-hidden rounded-[28px] bg-panel">
      <VideoWallpaper className="absolute inset-0 h-full w-full object-cover object-center" />

      <div
        className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"
        aria-hidden="true"
      />

      <div className="absolute inset-x-0 bottom-0 z-10 p-10 xl:p-12">
        <div className="max-w-md space-y-3">
          <p className="text-[1.65rem] font-semibold leading-[1.15] tracking-[-0.03em] text-balance text-white xl:text-[1.85rem]">
            Do briefing à publicação, no ritmo do estúdio.
          </p>
          <p className="max-w-sm text-[14px] leading-relaxed text-white/75">
            Referências e contexto do cliente no mesmo fluxo. A equipe decide;
            a ferramenta acelera.
          </p>
        </div>
      </div>
      </div>
    </aside>
  );
}

/**
 * Wallpaper em vídeo (qualidade original), loop nativo.
 * Prefer-reduced-motion → poster estático.
 */
function VideoWallpaper({ className }: { readonly className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [reduzirMovimento, definirReduzirMovimento] = useState(false);

  useEffect(() => {
    const consulta = globalThis.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    if (!consulta) return;
    const aplicar = () => definirReduzirMovimento(consulta.matches);
    aplicar();
    consulta.addEventListener("change", aplicar);
    return () => consulta.removeEventListener("change", aplicar);
  }, []);

  useEffect(() => {
    const video = ref.current;
    if (!video || reduzirMovimento) return;
    const tentar = () => {
      try {
        const resultado = video.play();
        if (resultado !== undefined && typeof resultado.catch === "function") {
          void resultado.catch(() => {
            /* autoplay pode falhar sem gesto */
          });
        }
      } catch {
        /* jsdom */
      }
    };
    tentar();
    video.addEventListener("loadeddata", tentar);
    return () => video.removeEventListener("loadeddata", tentar);
  }, [reduzirMovimento]);

  if (reduzirMovimento) {
    return (
      <img
        src={POSTER_WALLPAPER}
        alt=""
        className={className}
        decoding="async"
      />
    );
  }

  return (
    <video
      ref={ref}
      className={className}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={POSTER_WALLPAPER}
      disablePictureInPicture
      disableRemotePlayback
      aria-hidden="true"
    >
      <source src={VIDEO_WALLPAPER} type="video/mp4" />
    </video>
  );
}
